import Phaser from 'phaser';
import { GAME_CONFIG } from '../config/game';
import { getSoundSystem } from '../systems/SoundSystem';
import { getMusicPlayer } from '../systems/MusicPlayer';
import { getAllDailyScores } from '../systems/DailySave';
import { BackHandler, pushSceneState } from '../systems/BackButton';

const DAY_NAMES = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const CELL_SIZE = 50;
const GRID_COLS = 7;
const GRID_TOP = 120;
const ACCENT_COLOR = '#f0d060';

export class DailyCalendarScene extends Phaser.Scene implements BackHandler {
  private viewYear = 0;
  private viewMonth = 0;
  private monthLabel!: Phaser.GameObjects.Text;
  private leftArrow!: Phaser.GameObjects.Text;
  private rightArrow!: Phaser.GameObjects.Text;
  private calendarContainer!: Phaser.GameObjects.Container;
  private popupContainer: Phaser.GameObjects.Container | null = null;
  private scores: Record<string, number> = {};

  constructor() {
    super('DailyCalendarScene');
  }

  handleBack(): void {
    this.scene.start('TitleScene');
  }

  init(): void {
    const now = new Date();
    this.viewYear = now.getFullYear();
    this.viewMonth = now.getMonth();
  }

  create(): void {
    pushSceneState();
    getMusicPlayer().startMenu();
    this.scores = getAllDailyScores();

    const centerX = GAME_CONFIG.width / 2;

    this.add.text(centerX, 40, 'Daily Challenge', {
      fontSize: '24px',
      color: ACCENT_COLOR,
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    const closeBtn = this.add.text(GAME_CONFIG.width - 28, 30, '✕', {
      fontSize: '28px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5).setInteractive();

    closeBtn.on('pointerdown', () => {
      getSoundSystem().playBlip();
      this.scene.start('TitleScene');
    });

    this.monthLabel = this.add.text(centerX, 80, '', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    this.leftArrow = this.add.text(40, 80, '‹', {
      fontSize: '48px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5).setInteractive();

    this.leftArrow.on('pointerdown', () => {
      getSoundSystem().playBlip();
      this.prevMonth();
    });

    this.rightArrow = this.add.text(GAME_CONFIG.width - 40, 80, '›', {
      fontSize: '48px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5).setInteractive();

    this.rightArrow.on('pointerdown', () => {
      getSoundSystem().playBlip();
      this.nextMonth();
    });

    this.calendarContainer = this.add.container(0, 0);
    this.renderMonth();
  }

  private prevMonth(): void {
    this.viewMonth--;
    if (this.viewMonth < 0) {
      this.viewMonth = 11;
      this.viewYear--;
    }
    this.renderMonth();
  }

  private nextMonth(): void {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    if (this.viewYear === currentYear && this.viewMonth === currentMonth) return;

    this.viewMonth++;
    if (this.viewMonth > 11) {
      this.viewMonth = 0;
      this.viewYear++;
    }
    this.renderMonth();
  }

  private renderMonth(): void {
    this.calendarContainer.removeAll(true);
    if (this.popupContainer) {
      this.popupContainer.destroy();
      this.popupContainer = null;
    }

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const today = now.getDate();

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    this.monthLabel.setText(`${monthNames[this.viewMonth]} ${this.viewYear}`);

    const isCurrentMonth = this.viewYear === currentYear && this.viewMonth === currentMonth;
    const canGoForward = !isCurrentMonth;
    this.rightArrow.setAlpha(canGoForward ? 1 : 0.2);

    const centerX = GAME_CONFIG.width / 2;
    const gridWidth = GRID_COLS * CELL_SIZE;
    const gridLeft = centerX - gridWidth / 2;

    for (let i = 0; i < GRID_COLS; i++) {
      const x = gridLeft + i * CELL_SIZE + CELL_SIZE / 2;
      const label = this.add.text(x, GRID_TOP, DAY_NAMES[i], {
        fontSize: '14px',
        color: '#888888',
        fontFamily: 'sans-serif',
      }).setOrigin(0.5);
      this.calendarContainer.add(label);
    }

    const firstDay = new Date(this.viewYear, this.viewMonth, 1).getDay();
    const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();

    for (let day = 1; day <= daysInMonth; day++) {
      const index = firstDay + day - 1;
      const col = index % 7;
      const row = Math.floor(index / 7);
      const x = gridLeft + col * CELL_SIZE + CELL_SIZE / 2;
      const y = GRID_TOP + 30 + row * CELL_SIZE + CELL_SIZE / 2;

      const isFuture = isCurrentMonth && day > today;
      const isToday = isCurrentMonth && day === today;
      const dateStr = this.formatDate(this.viewYear, this.viewMonth, day);
      const hasScore = dateStr in this.scores;

      if (isToday) {
        const ring = this.add.graphics();
        ring.lineStyle(2, 0xf0d060);
        ring.strokeCircle(x, y, 20);
        this.calendarContainer.add(ring);
      }

      if (hasScore) {
        const bg = this.add.graphics();
        bg.fillStyle(0xf0d060, 0.2);
        bg.fillCircle(x, y, 18);
        this.calendarContainer.add(bg);
      }

      const color = isFuture ? '#444444' : hasScore ? ACCENT_COLOR : '#ffffff';
      const dayText = this.add.text(x, y, String(day), {
        fontSize: '16px',
        color,
        fontFamily: 'sans-serif',
      }).setOrigin(0.5);
      this.calendarContainer.add(dayText);

      if (!isFuture) {
        const hitArea = this.add.rectangle(x, y, CELL_SIZE - 4, CELL_SIZE - 4, 0x000000, 0);
        hitArea.setInteractive();
        hitArea.on('pointerdown', () => {
          getSoundSystem().playBlip();
          this.showPopup(dateStr, hasScore ? this.scores[dateStr] : null);
        });
        this.calendarContainer.add(hitArea);
      }
    }
  }

  private showPopup(date: string, score: number | null): void {
    if (this.popupContainer) {
      this.popupContainer.destroy();
      this.popupContainer = null;
    }

    const centerX = GAME_CONFIG.width / 2;
    const centerY = GAME_CONFIG.height / 2;

    this.popupContainer = this.add.container(centerX, centerY);

    const bg = this.add.graphics();
    bg.fillStyle(0x222244, 0.95);
    bg.fillRoundedRect(-140, -100, 280, 200, 16);
    bg.lineStyle(2, 0xf0d060);
    bg.strokeRoundedRect(-140, -100, 280, 200, 16);
    this.popupContainer.add(bg);

    const dateText = this.add.text(0, -65, date, {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);
    this.popupContainer.add(dateText);

    if (score !== null) {
      const scoreText = this.add.text(0, -30, `Best: ${score}`, {
        fontSize: '16px',
        color: ACCENT_COLOR,
        fontFamily: 'sans-serif',
      }).setOrigin(0.5);
      this.popupContainer.add(scoreText);
    } else {
      const newText = this.add.text(0, -30, 'New Challenge', {
        fontSize: '16px',
        color: '#aaaaaa',
        fontFamily: 'sans-serif',
      }).setOrigin(0.5);
      this.popupContainer.add(newText);
    }

    const playBtn = this.add.text(0, 25, 'Play', {
      fontSize: '22px',
      color: '#1a1a2e',
      fontFamily: 'sans-serif',
      backgroundColor: ACCENT_COLOR,
      padding: { x: 40, y: 12 },
    }).setOrigin(0.5).setInteractive();
    this.popupContainer.add(playBtn);

    playBtn.on('pointerdown', () => {
      getSoundSystem().playBlip();
      this.scene.start('GameScene', { daily: true, date });
    });

    const closeBtn = this.add.text(120, -80, '✕', {
      fontSize: '22px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5).setInteractive();
    this.popupContainer.add(closeBtn);

    closeBtn.on('pointerdown', () => {
      getSoundSystem().playBlip();
      this.popupContainer?.destroy();
      this.popupContainer = null;
    });
  }

  private formatDate(year: number, month: number, day: number): string {
    const m = String(month + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${year}-${m}-${d}`;
  }
}
